import {
  createRootRoute,
  createRoute,
  createRouter,
  Outlet,
  redirect,
} from "@tanstack/react-router";
import { DashboardLayout } from "./components/layout/DashboardLayout";
import { SettingsLayout } from "./components/layout/SettingsLayout";
import { requireAdmin, requireAuth, requireFullAccess } from "./lib/route-guards";
import { RootLayout } from "./routes/__root";
import { AccountDetailPage } from "./routes/dashboard/accounts/$accountId";
import { AccountsPage } from "./routes/dashboard/accounts/index";
import { BenchPage } from "./routes/dashboard/bench/index";
import { ClientPartnersPage } from "./routes/dashboard/client-partners/index";
import { DashboardPage } from "./routes/dashboard/index";
import { InternalTimePage } from "./routes/dashboard/internal-time/index";
import { ProfilePage } from "./routes/dashboard/profile";
import { ProjectDetailPage } from "./routes/dashboard/projects/$demandId";
import { ProjectsPage } from "./routes/dashboard/projects/index";
import { RiskPage } from "./routes/dashboard/risk/index";
import { SettingsAccessPage } from "./routes/dashboard/settings/access";
import { SettingsAuditPage } from "./routes/dashboard/settings/audit";
import { SettingsEmailPage } from "./routes/dashboard/settings/email";
import { SettingsSystemPage } from "./routes/dashboard/settings/system";
import { SettingsUsersPage } from "./routes/dashboard/settings/users";
import { TechnologyPage } from "./routes/dashboard/technology/index";
import { ThisMonthPage } from "./routes/dashboard/this-month/index";
import { LoginPage } from "./routes/login";
import { SignupPage } from "./routes/signup";

const rootRoute = createRootRoute({ component: RootLayout });

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  beforeLoad: () => {
    throw redirect({ to: "/login" });
  },
});

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/login",
  component: LoginPage,
});

const signupRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/signup",
  component: SignupPage,
});

function DashboardLayoutRoute() {
  return (
    <DashboardLayout>
      <Outlet />
    </DashboardLayout>
  );
}

const dashboardLayout = createRoute({
  getParentRoute: () => rootRoute,
  path: "/dashboard",
  beforeLoad: requireAuth,
  component: DashboardLayoutRoute,
});

// The reporting range lives in the URL, not in component state: drilling from
// the list into an account has to keep showing the same months, and a pasted
// link has to reproduce what the sender saw.
const validateRangeSearch = (
  search: Record<string, unknown>,
): { range?: string; contract_type?: string; tab?: string } => {
  const range = search.range;
  const contractType = search.contract_type;
  const tab = search.tab;
  return {
    ...(typeof range === "string" && range.length > 0 ? { range } : {}),
    ...(typeof contractType === "string" && contractType.length > 0
      ? { contract_type: contractType }
      : {}),
    ...(typeof tab === "string" && tab.length > 0 ? { tab } : {}),
  };
};

const dashboardIndex = createRoute({
  getParentRoute: () => dashboardLayout,
  path: "/",
  // Company-wide: refused to Aggregate users, who land on their accounts.
  beforeLoad: requireFullAccess,
  component: DashboardPage,
  validateSearch: validateRangeSearch,
});

const profileRoute = createRoute({
  getParentRoute: () => dashboardLayout,
  path: "/profile",
  component: ProfilePage,
});

const projectsRoute = createRoute({
  getParentRoute: () => dashboardLayout,
  path: "/projects",
  component: ProjectsPage,
  validateSearch: validateRangeSearch,
});

const accountsRoute = createRoute({
  getParentRoute: () => dashboardLayout,
  path: "/accounts",
  component: AccountsPage,
  validateSearch: validateRangeSearch,
});

const clientPartnersRoute = createRoute({
  getParentRoute: () => dashboardLayout,
  path: "/client-partners",
  // Company-wide: refused to Aggregate users, who land on their accounts.
  beforeLoad: requireFullAccess,
  component: ClientPartnersPage,
  validateSearch: validateRangeSearch,
});

// The risk board: three buckets behind ?tab=, the range behind ?range=, both
// in the URL so a link someone pastes reproduces the screen they were reading.
const riskRoute = createRoute({
  getParentRoute: () => dashboardLayout,
  path: "/risk",
  component: RiskPage,
  validateSearch: validateRangeSearch,
});

// The open month, live. No range (it is always the month in progress); the
// table's filters ride in ?f_ like the project page's.
const thisMonthRoute = createRoute({
  getParentRoute: () => dashboardLayout,
  path: "/this-month",
  component: ThisMonthPage,
  validateSearch: validateRangeSearch,
});

const technologyRoute = createRoute({
  getParentRoute: () => dashboardLayout,
  path: "/technology",
  // Company-wide: refused to Aggregate users, who land on their accounts.
  beforeLoad: requireFullAccess,
  component: TechnologyPage,
  validateSearch: validateRangeSearch,
});

const benchRoute = createRoute({
  getParentRoute: () => dashboardLayout,
  path: "/bench",
  // Company-wide: refused to Aggregate users, who land on their accounts.
  beforeLoad: requireFullAccess,
  component: BenchPage,
  validateSearch: validateRangeSearch,
});

const internalTimeRoute = createRoute({
  getParentRoute: () => dashboardLayout,
  path: "/internal-time",
  // Company-wide: refused to Aggregate users, who land on their accounts.
  beforeLoad: requireFullAccess,
  component: InternalTimePage,
  validateSearch: validateRangeSearch,
});

const accountDetailRoute = createRoute({
  getParentRoute: () => dashboardLayout,
  path: "/accounts/$accountId",
  component: AccountDetailPage,
  validateSearch: validateRangeSearch,
});

const projectDetailRoute = createRoute({
  getParentRoute: () => dashboardLayout,
  path: "/projects/$demandId",
  component: ProjectDetailPage,
  validateSearch: validateRangeSearch,
});

function SettingsLayoutRoute() {
  return (
    <SettingsLayout>
      <Outlet />
    </SettingsLayout>
  );
}

const settingsLayout = createRoute({
  getParentRoute: () => dashboardLayout,
  path: "/settings",
  beforeLoad: async (ctx) => {
    await requireAdmin();
    if (ctx.location.pathname === "/dashboard/settings") {
      throw redirect({ to: "/dashboard/settings/access" });
    }
  },
  component: SettingsLayoutRoute,
});

const settingsAccessRoute = createRoute({
  getParentRoute: () => settingsLayout,
  path: "/access",
  component: SettingsAccessPage,
});

const settingsUsersRoute = createRoute({
  getParentRoute: () => settingsLayout,
  path: "/users",
  component: SettingsUsersPage,
});

const settingsSystemRoute = createRoute({
  getParentRoute: () => settingsLayout,
  path: "/system",
  component: SettingsSystemPage,
});

const settingsEmailRoute = createRoute({
  getParentRoute: () => settingsLayout,
  path: "/email",
  component: SettingsEmailPage,
});

const settingsAuditRoute = createRoute({
  getParentRoute: () => settingsLayout,
  path: "/audit",
  component: SettingsAuditPage,
});

const routeTree = rootRoute.addChildren([
  indexRoute,
  loginRoute,
  signupRoute,
  dashboardLayout.addChildren([
    dashboardIndex,
    profileRoute,
    projectsRoute,
    projectDetailRoute,
    accountsRoute,
    accountDetailRoute,
    clientPartnersRoute,
    riskRoute,
    thisMonthRoute,
    technologyRoute,
    benchRoute,
    internalTimeRoute,
    settingsLayout.addChildren([
      settingsAccessRoute,
      settingsUsersRoute,
      settingsSystemRoute,
      settingsEmailRoute,
      settingsAuditRoute,
    ]),
  ]),
]);

export const router = createRouter({ routeTree });

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
